import React, { useEffect, useState } from 'react';
import { Users, Bell, BookOpen, FileText, Video, Star, TrendingUp, Clock, CheckCircle, XCircle } from 'lucide-react';
import API from '../utils/api';

export default function Dashboard() {
  const [data, setData] = useState({ batches:[], notices:[], courses:[], applications:[], videos:[], stories:[] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get('/batches'), API.get('/notices'), API.get('/courses'),
      API.get('/applications'), API.get('/videos'), API.get('/success-stories')
    ]).then(([b,n,c,a,v,s]) => {
      setData({ batches:b.data, notices:n.data, courses:c.data, applications:a.data, videos:v.data, stories:s.data });
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-red-700 border-t-transparent"/></div>;

  const pending  = data.applications.filter(a=>a.status==='pending').length;
  const approved = data.applications.filter(a=>a.status==='approved').length;
  const rejected = data.applications.filter(a=>a.status==='rejected').length;
  const ongoing  = data.batches.filter(b=>b.status==='ongoing').length;

  const stats = [
    { label:'Total Batches',    value:data.batches.length,      sub:`${ongoing} ongoing`,       icon:Users,    color:'bg-blue-500',   badge:'bg-blue-50 text-blue-700' },
    { label:'Notices',          value:data.notices.length,      sub:`${data.notices.filter(n=>n.isImportant).length} urgent`, icon:Bell, color:'bg-yellow-500', badge:'bg-yellow-50 text-yellow-700' },
    { label:'Courses',          value:data.courses.length,      sub:'Active courses',           icon:BookOpen, color:'bg-green-500',  badge:'bg-green-50 text-green-700' },
    { label:'Applications',     value:data.applications.length, sub:`${pending} pending`,       icon:FileText, color:'bg-purple-500', badge:'bg-purple-50 text-purple-700' },
    { label:'Videos',           value:data.videos.length,       sub:'YouTube videos',           icon:Video,    color:'bg-pink-500',   badge:'bg-pink-50 text-pink-700' },
    { label:'Success Stories',  value:data.stories.length,      sub:'Student stories',          icon:Star,     color:'bg-orange-500', badge:'bg-orange-50 text-orange-700' },
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center"><TrendingUp className="text-red-700" size={22}/></div>
        <div><h1 className="text-xl font-bold text-slate-800">Dashboard</h1><p className="text-slate-500 text-sm">Skill to Japan BD — Overview</p></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {stats.map(({label,value,sub,icon:Icon,color,badge})=>(
          <div key={label} className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 flex items-center gap-4">
            <div className={`${color} p-3 rounded-xl`}><Icon className="text-white" size={22}/></div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{value}</p>
              <p className="text-slate-600 text-xs font-medium">{label}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${badge} mt-1 inline-block`}>{sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-semibold text-slate-700 mb-4 text-sm">Application Summary</h3>
          {[{label:'Pending',  count:pending,  icon:Clock,        c:'text-yellow-500 bg-yellow-50'},
            {label:'Approved', count:approved, icon:CheckCircle,  c:'text-green-500 bg-green-50'},
            {label:'Rejected', count:rejected, icon:XCircle,      c:'text-red-500 bg-red-50'}].map(({label,count,icon:Icon,c})=>(
            <div key={label} className="flex items-center justify-between mb-3 last:mb-0">
              <div className="flex items-center gap-2"><div className={`p-1.5 rounded-lg ${c}`}><Icon size={14}/></div><span className="text-sm text-slate-600">{label}</span></div>
              <span className="font-bold text-slate-800">{count}</span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-semibold text-slate-700 mb-4 text-sm">Batch Status</h3>
          {[{label:'Upcoming',  key:'upcoming',  c:'bg-blue-100 text-blue-700'},
            {label:'Ongoing',   key:'ongoing',   c:'bg-green-100 text-green-700'},
            {label:'Completed', key:'completed', c:'bg-slate-100 text-slate-600'}].map(({label,key,c})=>(
            <div key={key} className="flex items-center justify-between mb-3 last:mb-0">
              <span className="text-sm text-slate-600">{label}</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${c}`}>{data.batches.filter(b=>b.status===key).length}</span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-semibold text-slate-700 mb-4 text-sm">Recent Notices</h3>
          {data.notices.slice(0,4).map(n=>(
            <div key={n._id} className="flex items-start gap-2 mb-2 last:mb-0">
              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.isImportant?'bg-red-500':'bg-green-400'}`}/>
              <p className="text-xs text-slate-600 line-clamp-2">{n.title}</p>
            </div>
          ))}
          {data.notices.length===0&&<p className="text-xs text-slate-400">No notices yet</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-50 flex items-center justify-between">
          <h3 className="font-semibold text-slate-700 text-sm">Recent Applications</h3>
          <span className="text-xs text-slate-400">Latest 5</span>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>{['Name','Phone','Course','Batch','Status'].map(h=><th key={h} className="text-left py-3 px-4">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-sm">
            {data.applications.slice(0,5).map(a=>(
              <tr key={a._id} className="hover:bg-slate-50">
                <td className="py-3 px-4 font-medium text-slate-700">{a.name}</td>
                <td className="py-3 px-4 text-slate-500">{a.phone}</td>
                <td className="py-3 px-4 text-slate-500">{a.courseInterest}</td>
                <td className="py-3 px-4 text-slate-500">{a.batchRef?.batchName||'—'}</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${a.status==='pending'?'bg-yellow-100 text-yellow-700':a.status==='approved'?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>
                    {a.status.charAt(0).toUpperCase()+a.status.slice(1)}
                  </span>
                </td>
              </tr>
            ))}
            {data.applications.length===0&&<tr><td colSpan="5" className="text-center py-8 text-slate-400">No applications yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
